import { useLoaderData } from "@remix-run/react";
import { LoaderFunction } from "@remix-run/server-runtime";
import clamp from "lodash/clamp";
import QueryString from "qs";
import { useEffect } from "react";
import invariant from "tiny-invariant";
import { ClientSide } from "~/@types";
import {
  IExpert,
  IExpertFields,
  IInsight,
  INewsletter,
  SpecificLocale,
} from "~/@types/generated/contentful";
import CardList from "~/components/Cards/CardList";
import { ExpertHero } from "~/components/experts";
import { FourOFourCaughtData } from "~/components/shared/FourOFour";
import Newsletter from "~/components/shared/newsletter/Newsletter";
import { generateBreadcrumbFromRequest } from "~/components/utils/breadcrumbSchema.server";
import { getTwicPicsURL } from "~/components/utils/getTwicPicsURL";
import { pageView, track } from "~/components/utils/gtm.client";
import SchemaOrg from "~/components/utils/SchemaOrg";
import {
  getExpertSchema,
  SchemaOrgType,
} from "~/components/utils/schemaOrgUtils.server";
import { INSIGHT_CARD_GROUP_SIZE } from "~/config";
import {
  getEntryBySlug,
  getLinksToEntry,
  getNewsletter,
} from "~/contentful/index.server";
import * as routeUtils from "~/contentful/route-utils";
import { ComponentDataProvider } from "~/contexts/ComponentDataProvider";
import { getActiveExperts } from "~/contexts/dataUtils/getActiveExperts";
import { i18n } from "~/i18n.server";

export const meta = routeUtils.meta;

export const handle = routeUtils.handle("expert");

interface LoaderData {
  expert: ClientSide<IExpert>;
  insights: ClientSide<IInsight>[];
  totalInsights: number;
  experts: ClientSide<IExpertFields>[];
  newsletter: ClientSide<INewsletter>;
  meta: routeUtils.MetaInfo;
  schemaOrg: SchemaOrgType;
  breadcrumbSchema: SchemaOrgType | null;
}

export const loader: LoaderFunction = async ({ params, request }) => {
  const locale = await i18n.getLocale(request);

  const invariantMessage =
    "expected slug to be passed as an argument or params.slug to be defined";
  const slugToQuery = params.expertSlug;

  invariant(slugToQuery, invariantMessage);

  const expert = (await getEntryBySlug("expert", slugToQuery, {
    locale,
  })) as SpecificLocale<IExpert>;

  if (!expert) {
    throw new Response(
      // This is me cheating as a way to pass the color through
      JSON.stringify({
        message: "Expert Not Found",
        backgroundColor: "white",
        notFound: "expert",
      } as FourOFourCaughtData),
      { status: 404 },
    );
  }

  const url = new URL(request.url);
  const searchParams = QueryString.parse(url.search.slice(1));
  const page = searchParams.page ? Number(searchParams.page) : 1;

  const insightsCollection = await getLinksToEntry(expert.sys.id, locale, {
    content_type: "insight",
    order: "-fields.date",
    // Limit: If the page number corresponds to more articles than the number that are loaded,
    // the InsightsCardGroupFetcher in Cardlist will load the rest on the client side.
    // Avoids large payload size.
    limit: clamp(page * INSIGHT_CARD_GROUP_SIZE, 0, 96),
    // Cut down on data size. Article content not needed.
    select: [
      "sys.id",
      "fields.title",
      "fields.slug",
      "fields.image",
      "fields.experts",
      "fields.topic",
      "fields.date",
      "fields.embargoed",
    ],
    excludeEmbargoed: true,
  });

  const experts = await getActiveExperts(locale);
  const newsletter = await getNewsletter(locale);

  const fullName = `${expert.fields.firstName} ${expert.fields.lastName}`;

  const openGraphImageUrl = expert.fields.image?.fields
    ? getTwicPicsURL(expert.fields.image.fields, { width: 1200, format: "jpg" })
    : "";

  const breadcrumbSchema = generateBreadcrumbFromRequest({
    request,
    expertName: fullName,
  });

  return {
    expert,
    insights: insightsCollection.items,
    totalInsights: insightsCollection.total,
    experts,
    newsletter,
    meta: routeUtils.getMetaInfo({
      title: fullName,
      description: expert.fields.role ?? fullName,
      requestUrl: request.url,
      openGraphImageUrl,
    }),
    schemaOrg: getExpertSchema(expert),
    breadcrumbSchema,
  } as LoaderData;
};

export default function ExpertPage() {
  const {
    expert,
    insights,
    totalInsights,
    experts,
    newsletter,
    schemaOrg,
    breadcrumbSchema,
  } = useLoaderData<LoaderData>();

  useEffect(() => {
    track(
      pageView({
        category: "Expert",
        name: expert.fields.firstName + " " + expert.fields.lastName,
      }),
    );
  }, [expert]);

  return (
    <ComponentDataProvider
      componentData={{
        insightsList: {
          [expert.sys.id]: insights,
        },
        totalInsights,
        experts,
      }}
    >
      <SchemaOrg
        content={breadcrumbSchema ? [schemaOrg, breadcrumbSchema] : [schemaOrg]}
      />
      <div>
        <ExpertHero expert={expert} />
        {insights.length ? (
          <section className="w-full">
            <div className="tbi-grid">
              <hr className="col-span-full mb-4 border-black lg:mt-20" />
              <p className="eyebrows col-span-full -mb-10">
                {`Insights by ${expert.fields.firstName} ${
                  expert.fields.lastName
                } (${totalInsights || 0})`}
              </p>
            </div>
            <CardList relatedEntry={expert} />
          </section>
        ) : null}
        {/* <section className="w-full">
          <ExpertsCarousel sysId={expert.fields.slug} />
        </section> */}
      </div>
      {newsletter?.fields && <Newsletter fields={newsletter.fields} />}
    </ComponentDataProvider>
  );
}

export const ErrorBoundary = routeUtils.ContentfulErrorBoundary;

export const shouldRevalidate = routeUtils.shouldRevalidate;
